/**
 * useNavbarState Composable
 *
 * Centralizes all navbar reactive state, route helpers, company switching logic,
 * and notification dot computeds. Used by navBar.vue and its child nav components.
 */

import { inject, computed, ref, watch, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useBunnyUrl } from '@/composables/useBunnyUrl'
import { BUNNY_URL_INTENT } from '@/utils/bunnyConstants'
import { useUserAuthStore } from '@/stores/userAuthStore'
import { defaultUserRoute } from '@/utils/userRouting'
import { useViewModeStore } from '@/stores/viewModeStore'
import { useStoreSelector } from '@/composables/useStoreSelector'
import { useImpersonationService } from '@/services/impersonationService'
import { useAdminCompaniesStore } from '@/stores/adminCompaniesStore'
import { useClientProjectsStore } from '@/stores/clientProjectsStore'
import { useClientCalendarStore } from '@/stores/clientCalendarStore'
import { useStorageSubscriptionStore } from '@/stores/storageSubscriptionStore'
import { useNotificationsStore } from '@/stores/notificationsStore'
import { useDGFReportStore } from '@/stores/dgfReportStore'
import { useAdminAutomationRulesStore } from '@/stores/adminAutomationRulesStore'
import { useClientReviewStore } from '@/stores/clientReviewStore'
import { useClientShowsStore } from '@/stores/clientShowsStore'
import { companyCache } from '@/services/companyCache'
import { logger } from '@/services/loggerService'
import { PROJECT_STATUS } from '@/utils/projectUtils'
import { isAdminUser } from '@/utils/userUtils'

// Module-level shared state (shared across all useNavbarState() instances)
const isMobileNotificationsOpen = ref(false)
const isCompanySwitcherOpen = ref(false)

/**
 * Open the company spotlight from outside the navbar (e.g. the "Viewing as"
 * banner in App.vue) without instantiating the whole composable, which would
 * register its ⌥C shortcut a second time. Companies load via the watcher below.
 */
export function openCompanySwitcherGlobal() {
  isCompanySwitcherOpen.value = true
}

export function useNavbarState() {
  const route = useRoute()
  const router = useRouter()
  const user = inject('user')
  const company = inject('company')

  const userAuthStore = useUserAuthStore()
  const viewModeStore = useViewModeStore()
  const { activeStores } = useStoreSelector()
  const { stopImpersonation, startImpersonation, switchImpersonation } =
    useImpersonationService()
  const adminCompaniesStore = useAdminCompaniesStore()
  const clientProjectsStore = useClientProjectsStore()
  const clientCalendarStore = useClientCalendarStore()
  const storageSubscriptionStore = useStorageSubscriptionStore()
  const notificationsStore = useNotificationsStore()
  const dgfReportStore = useDGFReportStore()
  const automationStore = useAdminAutomationRulesStore()

  // ── Reactive state ──────────────────────────────────────────────
  const isMoreMenuActive = ref(false)

  // ── Current company ─────────────────────────────────────────────
  const currentCompany = computed(() => {
    if (viewModeStore.isImpersonating) {
      const companyId = viewModeStore.impersonatedCompany
      const companyFromStore = activeStores.value.companyStore.currentCompany
      if (companyFromStore && companyFromStore.id === companyId) {
        return companyFromStore
      }
      const cachedCompany = companyCache.getCompany(companyId)
      if (cachedCompany) return cachedCompany
      return { id: companyId }
    }

    if (company?.value) return company.value

    if (user?.value?.type === 'user' && user?.value?.companyId) {
      const companyFromStore = activeStores.value.companyStore.currentCompany
      if (companyFromStore && companyFromStore.id === user.value.companyId) {
        return companyFromStore
      }
      const cachedCompany = companyCache.getCompany(user.value.companyId)
      if (cachedCompany) return cachedCompany
      return { id: user.value.companyId }
    }

    return null
  })

  // ── Route-derived computeds ─────────────────────────────────────
  const isNexus = computed(() => {
    return route.path.startsWith('/nexus') && !viewModeStore.isImpersonating
  })

  // The agency PORTAL (managing clients), not the agency's own company portal —
  // their own brand's work lives under /accounts/:agencyId like any company.
  const isAgencyPortal = computed(() => route.path.startsWith('/agency/'))

  const currentRouteCompanyId = computed(() => {
    if (route.params.id) return route.params.id
    return viewModeStore.isImpersonating
      ? viewModeStore.impersonatedCompany
      : currentCompany.value?.id || user?.value?.companyId
  })

  const showClientNav = computed(() => {
    return (
      !isNexus.value &&
      !isAgencyPortal.value &&
      ((currentCompany.value && currentCompany.value.id) ||
        viewModeStore.isImpersonating)
    )
  })

  const logoLinkDestination = computed(() => {
    // In Nexus the brandmark goes wherever "home" is for this admin — the same
    // landing page they set on My Profile and get dropped on at sign-in.
    if (isNexus.value) {
      return defaultUserRoute(userAuthStore.user) || { name: 'admin-my-work' }
    }

    return {
      name: isAgencyPortal.value
        ? 'agency-companies'
        : currentCompany.value?.id
          ? 'account-assets'
          : 'admin-my-work',
      params: isAgencyPortal.value
        ? { agencyId: route.params.agencyId }
        : currentCompany.value?.id
          ? { id: currentCompany.value.id }
          : {},
    }
  })

  // ── Logo computeds ──────────────────────────────────────────────
  const defaultLogoPath = '/assets/img/vysual.png'
  // Square brandmark — used where the area is deliberately not company-branded
  // and the full wordmark would compete with a dark navbar.
  const brandMarkPath = '/assets/img/vysual-brand-mark.svg'

  // Bunny logo URLs are SIGNED with a ~1h TTL — the value persisted on
  // company.logoUrl (and in companyCache) 403s once it expires. Resolve a
  // FRESH signed URL at render time from logoAssetId; legacy Firebase logos
  // (no assetId) fall through to the stored logoUrl unchanged.
  const { url: resolvedLogoUrl, refresh: refreshLogoUrl } = useBunnyUrl(() => ({
    assetId: currentCompany.value?.logoAssetId,
    fallback: currentCompany.value?.logoUrl,
    intent: BUNNY_URL_INTENT.THUMBNAIL,
  }))

  const currentLogo = computed(() => {
    if (!userAuthStore.user) return defaultLogoPath
    // Both staff-side portals run on the brandmark + a mode label rather than a
    // company logo: neither is scoped to one company, so a wordmark or client
    // logo would misdescribe where you are.
    if (isNexus.value || isAgencyPortal.value) return brandMarkPath
    return resolvedLogoUrl.value || defaultLogoPath
  })

  const isCurrentLogoSvg = computed(() => {
    const logoUrl = currentLogo.value
    return (
      logoUrl &&
      (logoUrl.toLowerCase().includes('.svg') ||
        logoUrl.toLowerCase().includes('image/svg') ||
        logoUrl.toLowerCase().includes('svg+xml'))
    )
  })

  /** Which portal the brandmark is standing in for. Null in a company portal,
   *  where the company's own logo already names the place. */
  const modeMarker = computed(() => {
    if (isNexus.value) return 'Nexus'
    if (isAgencyPortal.value) return 'Agency'
    return null
  })

  const companyName = computed(() => {
    return currentCompany.value ? currentCompany.value.name : 'Unknown Company'
  })

  const handleLogoLoad = (_event) => {
    // Image loaded successfully
  }

  // Re-mint at most once per company: a signed URL can expire between resolve
  // and render. Reset the guard whenever the active company changes.
  let logoRetried = false
  watch(() => currentCompany.value?.id, () => { logoRetried = false })

  const handleLogoError = async (error) => {
    const imgElement = error.target
    const logoUrl = imgElement?.src

    // First failure with a re-mintable Bunny asset: the token likely expired —
    // fetch a fresh signed URL and let the reactive src update before giving up.
    if (!logoRetried && currentCompany.value?.logoAssetId) {
      logoRetried = true
      await refreshLogoUrl()
      return
    }

    logger.warn('[NavBar] Logo load failed, using default logo', {
      originalUrl: logoUrl,
      isSvg: logoUrl?.toLowerCase().includes('.svg'),
      errorType: error?.type,
      errorMessage: error?.message,
      companyId: currentCompany.value?.id,
      companyName: currentCompany.value?.name,
      naturalWidth: imgElement?.naturalWidth,
      naturalHeight: imgElement?.naturalHeight,
    })

    if (imgElement && imgElement.src !== defaultLogoPath) {
      imgElement.src = defaultLogoPath
    }
  }

  // ── Current page context (for mobile navbar title) ─────────────
  const PAGE_CONTEXT_MAP = {
    'account-assets': { title: 'Assets', icon: ['fal', 'photo-film'] },
    'account-asset-detail': { title: 'Assets', icon: ['fal', 'photo-film'] },
    'account-upload': { title: 'Upload', icon: ['fal', 'cloud-upload'] },
    'account-sharing': { title: 'Sharing', icon: ['fal', 'share-nodes'] },
    'account-projects': { title: 'Projects', icon: ['fal', 'rectangle-list'] },
    'account-project-details': { title: 'Projects', icon: ['fal', 'rectangle-list'] },
    'account-reviews': { title: 'Reviews', icon: ['fal', 'list-check'] },
    'account-calendar': { title: 'Calendar', icon: ['fal', 'calendar'] },
    'account-company-details': { title: 'Company', icon: ['fal', 'building-user'] },
    'admin-calendar': { title: 'Calendar', icon: ['fal', 'calendar'] },
    ...Object.fromEntries(
      [
        'admin-companies',
        'admin-companies-create',
        'admin-companies-details',
        'admin-projects',
        'admin-podcasts',
        'admin-company-deliverables',
        'admin-company-contracts',
        'admin-company-storage',
        'admin-company-users',
        'admin-company-agency',
        'admin-company-activity',
        'admin-company-settings',
        'admin-project-edit',
      ].map((name) => [
        name,
        { title: 'Companies', icon: ['fal', 'building-user'] },
      ])
    ),
    'admin-my-work': { title: 'My Work', icon: ['fal', 'user-check'] },
    'admin-deliverables': { title: 'Deliverables', icon: ['fal', 'chart-kanban'] },
    'profile': { title: 'Profile', icon: ['fal', 'user-circle'] },
    'admin-profile': { title: 'Profile', icon: ['fal', 'user-circle'] },
  }

  const currentPageContext = computed(() => {
    if (!route.name) return null
    return PAGE_CONTEXT_MAP[route.name] || null
  })

  // ── Route active helpers ────────────────────────────────────────
  const isActiveRoute = (routePrefix) => {
    if (!route.name) return false

    const routeRelations = {
      'account-assets': ['account-assets', 'account-asset-detail'],
      'account-projects': ['account-projects', 'account-project-details'],
      'account-reviews': ['account-reviews'],
      'account-calendar': ['account-calendar'],
      'account-company-details': ['account-company-details'],
      'admin-deliverables': ['admin-deliverables'],
      'admin-users': ['admin-users'],
      // Projects and Podcasts live inside the company page now, so every one of
      // their routes should light up the Companies nav item.
      'admin-companies': [
        'admin-companies',
        'admin-companies-create',
        'admin-companies-details',
        'admin-projects',
        'admin-podcasts',
        'admin-company-deliverables',
        'admin-company-contracts',
        'admin-company-storage',
        'admin-company-users',
        'admin-company-agency',
        'admin-company-activity',
        'admin-company-settings',
        'admin-project-edit',
        'admin-project-review',
        'admin-show-episodes',
        'admin-episode-edit',
        'admin-blueprint-generator',
        'admin-discussion-guide-editor',
      ],
      'admin-calendar': ['admin-calendar'],
      'admin-form-builder': ['admin-form-builder'],
      'admin-pricing-config': ['admin-pricing-config'],
      'admin-scheduling-rules': ['admin-scheduling-rules'],
    }

    const relatedRoutes = routeRelations[routePrefix]
    if (relatedRoutes) return relatedRoutes.includes(route.name)
    return route.name.startsWith(routePrefix)
  }

  // Every page in the Reports dropdown, plus DGF's rule-config detail pages.
  // MSA Invoicing and Podcast Publishing were missing, so the nav item went
  // dark on two of its own reports.
  const isReportsActive = computed(() => {
    if (!route.name) return false
    return [
      'admin-asset-audit',
      'admin-user-access-report',
      'admin-loa-report',
      'admin-editor-stage-duration',
      'admin-msa-invoicing',
      'admin-podcast-publishing',
      'admin-delivery-risk',
      'admin-email-delivery',
      'admin-dgf-report',
      'admin-dgf-config',
      'admin-dgf-rule-create',
      'admin-dgf-rule-edit',
    ].includes(route.name)
  })

  // Every page reachable from the Utilities dropdown, plus the detail pages
  // that hang off them — the nav item was only lighting up on four of these.
  const isSettingsActive = computed(() => {
    if (!route.name) return false
    return [
      'admin-transcription',
      'admin-feedback',
      'admin-automation-rules',
      'admin-automation-rule-create',
      'admin-automation-rule-edit',
      'admin-workpool-settings',
      'admin-users',
      'admin-form-builder',
      'admin-pricing-config',
      'admin-scheduling-rules',
    ].includes(route.name)
  })

  const isAssetsActive = computed(() => {
    if (!route.name) return false
    return [
      'account-assets',
      'account-asset-detail',
      'account-upload',
      'account-sharing',
    ].includes(route.name)
  })

  // ── Notification dots ───────────────────────────────────────────
  const hasProjectsNeedingDates = computed(() => {
    if (isNexus.value || !showClientNav.value) return false

    const projectsNeedingDates = Object.values(
      clientProjectsStore.items || {}
    ).filter((project) => project.status === PROJECT_STATUS.DATES_NEEDED)

    const eventsNeedingDates = (clientCalendarStore.allEvents || []).filter(
      (event) =>
        event.companyId === currentRouteCompanyId.value &&
        event.clientSelectDate === true &&
        !event.date
    )

    return projectsNeedingDates.length > 0 || eventsNeedingDates.length > 0
  })

  const hasStorageActionRequired = computed(() => {
    if (isNexus.value || !showClientNav.value) return false
    const companyId = currentRouteCompanyId.value
    if (!companyId) return false
    return !!storageSubscriptionStore.gracePeriodStatus(companyId)
  })

  // ── Podcast shows (drives the "Podcasts" nav item) ─────────────
  const clientShowsStore = useClientShowsStore()

  // Source the companyId for shows from viewModeStore first when impersonating
  // — that flag is flipped before router.push during switchImpersonation, so
  // route.params.id can briefly lag and point at the previous company.
  const showsCompanyId = computed(() => {
    if (!showClientNav.value) return null
    if (viewModeStore.isImpersonating) return viewModeStore.impersonatedCompany
    return currentRouteCompanyId.value
  })

  // Fetch shows for the current company so the notification dot works
  watch(
    showsCompanyId,
    (companyId) => {
      if (companyId) {
        clientShowsStore.subscribeToCompanyShows(companyId)
      }
    },
    { immediate: true }
  )

  const hasShows = computed(() => {
    if (isNexus.value || !showClientNav.value) return false
    return Array.from(clientShowsStore.shows.values()).some(
      s => s.companyId === showsCompanyId.value
    )
  })

  // ── Review notification dot ────────────────────────────────────
  const clientReviewStore = useClientReviewStore()

  const hasActiveReviews = computed(() => {
    if (!clientReviewStore.initialized) return false
    return clientReviewStore.actionableItems.length > 0
  })

  // ── DGF + automation ────────────────────────────────────────────
  const dgfCounts = computed(() => dgfReportStore.counts)
  const dgfHasItems = computed(() => dgfReportStore.hasItems)
  const hasAutomationErrors = computed(() => automationStore.hasErrors)
  const automationErrorCount = computed(() => automationStore.totalErrorCount)

  // ── Notifications ───────────────────────────────────────────────
  const unreadNotificationCount = computed(() => notificationsStore.unreadCount)

  // ── Admin / Profile helpers ─────────────────────────────────────
  const isAdmin = computed(() => {
    return isAdminUser(userAuthStore.user)
  })

  const profileRouteName = computed(() => {
    return isAdmin.value ? 'admin-profile' : 'profile'
  })

  // ── Company switcher ────────────────────────────────────────────
  const ensureCompaniesLoaded = () => {
    if (isAdminUser(user.value)) {
      if (
        !adminCompaniesStore.initialized &&
        adminCompaniesStore.activeCompanies.length === 0
      ) {
        adminCompaniesStore.init().catch((error) => {
          logger.error('[NavBar] Failed to load companies:', error)
        })
      }
    }
  }

  // logoAssetId/logoUrl ride along so the switcher can render each company's
  // logo; isAgency decides which portal selecting it drops you into.
  const toSwitcherItem = (c, active) => ({
    id: c.id,
    name: c.name,
    active,
    isAgency: !!c.isAgency,
    agencyId: c.agencyId || null,
    logoAssetId: c.logoAssetId || null,
    logoUrl: c.logoUrl || null,
  })

  const availableCompanies = computed(() => [
    ...adminCompaniesStore.activeCompanies.map((c) => toSwitcherItem(c, true)),
    ...adminCompaniesStore.inactiveCompanies.map((c) => toSwitcherItem(c, false)),
  ])

  const handleCompanySelect = async (selectedCompany) => {
    const companyId =
      typeof selectedCompany === 'object' ? selectedCompany?.id : selectedCompany
    if (!companyId) return

    // An agency company's real home is its agency portal (managing its clients),
    // not the ordinary client portal at /accounts/:id. No impersonation: the
    // agency-portal guard admits staff on usertype, and App.vue's route watcher
    // tears down any impersonation still running when we leave /accounts/.
    const isAgency =
      typeof selectedCompany === 'object'
        ? !!selectedCompany?.isAgency
        : !!adminCompaniesStore.getCompanyById(companyId)?.isAgency
    if (isAgency) {
      router.push({ name: 'agency-companies', params: { agencyId: companyId } })
      closeCompanySwitcher()
      return
    }

    try {
      if (viewModeStore.isImpersonating) {
        await switchImpersonation(companyId)
      } else {
        await startImpersonation(companyId)
      }
      closeCompanySwitcher()
    } catch (error) {
      logger.error('[NavBar] Failed to switch company:', error)
    }
  }

  const handleStopImpersonation = async () => {
    // Staff reach the agency portal by plain navigation, never impersonation, so
    // stopImpersonation() hits its own not-impersonating early-return there and
    // the button does nothing. Navigate ourselves when it had nothing to undo.
    const wasImpersonating = viewModeStore.isImpersonating
    try {
      await stopImpersonation()
      if (!wasImpersonating) router.push({ name: 'admin-companies' })
      closeCompanySwitcher()
    } catch (error) {
      logger.error('[NavBar] Failed to stop impersonation:', error)
    }
  }

  // ── Mobile nav actions ──────────────────────────────────────────
  const toggleMoreMenu = () => {
    isMoreMenuActive.value = !isMoreMenuActive.value
  }

  const closeMoreMenu = () => {
    isMoreMenuActive.value = false
  }

  const openCompanySwitcher = () => {
    ensureCompaniesLoaded()
    isCompanySwitcherOpen.value = true
  }
  // Opened via openCompanySwitcherGlobal() too, so load companies on open.
  watch(isCompanySwitcherOpen, (open) => { if (open) ensureCompaniesLoaded() })

  const closeCompanySwitcher = () => {
    isCompanySwitcherOpen.value = false
  }

  const openMobileNotifications = () => {
    isMobileNotificationsOpen.value = true
    logger.info('[NavBar] Opening mobile notifications panel')
  }

  const closeMobileNotifications = () => {
    isMobileNotificationsOpen.value = false
  }

  // ── Other actions ───────────────────────────────────────────────
  const logOut = () => {
    userAuthStore.logout()
  }

  const handleSwitchAccount = () => {
    router.push({ name: 'account-selector' })
  }

  // ── Close-on-click-outside handler ──────────────────────────────
  const closeDropdown = (e) => {
    if (!e.target.closest('.navbar-item-dropdown')) {
      // No longer tracking isDropdownActive - removed dead ref
    }
    if (!e.target.closest('.more-menu')) {
      isMoreMenuActive.value = false
    }
  }

  const handleKeydown = (e) => {
    if (e.key === 'Escape' && isCompanySwitcherOpen.value) {
      closeCompanySwitcher()
    }
  }

  // ── Watchers ────────────────────────────────────────────────────
  // Watch for route changes to update notification company filter
  watch(
    [() => route.path, () => route.params.id, () => userAuthStore.isAdmin],
    ([path, routeCompanyId, isAdminUser]) => {
      if (isAdminUser) {
        notificationsStore.clearCompanyFilter()
        return
      }
      if (path.startsWith('/accounts/') && routeCompanyId) {
        notificationsStore.setCompanyFilter(routeCompanyId)
      } else {
        notificationsStore.clearCompanyFilter()
      }
    },
    { immediate: true }
  )

  // Ensure companies loaded when admin user auth state restores during impersonation
  watch(
    () => user.value?.type,
    (userType) => {
      if (
        (userType === 'admin' || userType === 'super') &&
        viewModeStore.isImpersonating
      ) {
        ensureCompaniesLoaded()
      }
    }
  )

  // ── Lifecycle ───────────────────────────────────────────────────
  onMounted(() => {
    document.addEventListener('click', closeDropdown)
    document.addEventListener('keydown', handleKeydown)

    if (isAdminUser(user.value)) {
      dgfReportStore.init()
      automationStore.initialize()

      if (viewModeStore.isImpersonating) {
        ensureCompaniesLoaded()
      }
    }
  })

  onUnmounted(() => {
    document.removeEventListener('click', closeDropdown)
    document.removeEventListener('keydown', handleKeydown)
    dgfReportStore.cleanup()
    automationStore.$reset()
  })

  return {
    // Injected refs
    user,
    company,

    // Stores (exposed for child components that need direct access)
    userAuthStore,
    viewModeStore,

    // Route-derived
    isNexus,
    isAgencyPortal,
    currentCompany,
    currentRouteCompanyId,
    showClientNav,
    logoLinkDestination,
    currentPageContext,

    // Logo
    currentLogo,
    modeMarker,
    isCurrentLogoSvg,
    companyName,
    handleLogoLoad,
    handleLogoError,

    // Route active helpers
    isActiveRoute,
    isReportsActive,
    isSettingsActive,
    isAssetsActive,

    // Notification dots
    hasProjectsNeedingDates,
    hasStorageActionRequired,
    hasActiveReviews,
    hasShows,
    dgfCounts,
    dgfHasItems,
    hasAutomationErrors,
    automationErrorCount,

    // Notifications
    unreadNotificationCount,

    // Admin / Profile
    isAdmin,
    profileRouteName,

    // Company switcher
    availableCompanies,
    handleCompanySelect,
    handleStopImpersonation,
    ensureCompaniesLoaded,
    isCompanySwitcherOpen,
    openCompanySwitcher,
    closeCompanySwitcher,

    // Mobile nav
    isMoreMenuActive,
    toggleMoreMenu,
    closeMoreMenu,
    isMobileNotificationsOpen,
    openMobileNotifications,
    closeMobileNotifications,

    // Actions
    logOut,
    handleSwitchAccount,
  }
}
